<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  getVoiceCallProvider,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FreeswitchVoiceClient from 'dashboard/api/channel/voice/freeswitchVoiceClient';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const callsStore = useCallsStore();
const whatsappCallSession = useWhatsappCallSession();
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');
const { isCloudFeatureEnabled } = useAccount();

const showSimPickerDialog = ref(false);

// Filter all active voice / telephony lines in the tenant's account
const voiceInboxes = computed(() => {
  const all = inboxes.value || [];
  const lines = [];

  all.forEach(inbox => {
    const chType = inbox.channel_type || inbox.channelType;
    const provider = inbox.provider || inbox.channel?.provider;
    const telNum = inbox.telephony_number || inbox.telephonyNumber;
    const telNums = inbox.telephony_numbers || [];

    if (telNums.length > 0) {
      telNums.forEach(tn => {
        lines.push({
          id: inbox.id,
          inboxId: inbox.id,
          name: inbox.name,
          displayNumber: tn.phone_number || tn,
          displayName: `${tn.phone_number || tn} (${inbox.name})`,
          provider: 'freeswitch',
          callerId: tn.phone_number || tn,
        });
      });
    } else if (
      telNum ||
      provider === 'freeswitch' ||
      chType === 'Channel::Telephony' ||
      chType === 'Channel::Voice'
    ) {
      lines.push({
        id: inbox.id,
        inboxId: inbox.id,
        name: inbox.name,
        displayNumber: telNum || inbox.phone_number,
        displayName: telNum ? `${telNum} (${inbox.name})` : inbox.name,
        provider: 'freeswitch',
        callerId: telNum || inbox.phone_number,
      });
    }
  });

  return lines;
});

const hasVoiceAccount = computed(() => voiceInboxes.value.length > 0);

const contactPhoneNumber = computed(() => {
  return (
    props.chat?.meta?.sender?.phone_number ||
    props.chat?.sender?.phone_number ||
    props.chat?.meta?.sender?.phoneNumber ||
    null
  );
});

const voiceCallProvider = computed(() => getVoiceCallProvider(props.inbox));

// 1. WhatsApp Voice Inbox (WhatsApp calling)
const isWhatsappVoiceInbox = computed(
  () => voiceCallProvider.value === VOICE_CALL_PROVIDERS.WHATSAPP
);

// 2. WebWidget in-browser Audio Calling (dyte/webrtc on widget)
const isWebWidgetWithCalling = computed(() => {
  return (
    props.inbox?.channel_type === 'Channel::WebWidget' &&
    props.inbox?.selected_feature_flags?.includes('webrtc_calling')
  );
});

// 3. Whether FreeSWITCH calling should be used
const isFreeswitchCall = computed(() => {
  if (isWhatsappVoiceInbox.value) return false;
  // If web widget has widget calling enabled AND contact has no phone number, use widget calling
  if (isWebWidgetWithCalling.value && !contactPhoneNumber.value) return false;
  // If contact has a phone number AND account has a voice line, FreeSWITCH handles it
  if (contactPhoneNumber.value && hasVoiceAccount.value) return true;
  return voiceCallProvider.value === VOICE_CALL_PROVIDERS.FREESWITCH;
});

// Whether the call button should be visible in the conversation header
const isVoiceCallInbox = computed(() => {
  if (isWhatsappVoiceInbox.value) return true;
  if (isWebWidgetWithCalling.value) return true;
  // Only show phone call button for contacts if the account has a configured voice channel
  if (contactPhoneNumber.value && hasVoiceAccount.value) return true;
  if (voiceCallProvider.value !== null) return true;
  return false;
});

const isCallButtonDisabled = computed(() => {
  if (callsStore.hasActiveCall || callsStore.hasIncomingCall) return true;
  if (isWhatsappVoiceInbox.value) {
    return whatsappCallSession.isInitiating.value;
  }
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const isCallButtonLoading = computed(() =>
  isWhatsappVoiceInbox.value
    ? whatsappCallSession.isInitiating.value
    : !!contactsUiFlags.value?.isInitiatingCall
);

const callButtonTooltip = computed(() => {
  if (isWhatsappVoiceInbox.value) {
    return t('CONVERSATION.HEADER.WHATSAPP_CALL');
  }
  if (isWebWidgetWithCalling.value && !contactPhoneNumber.value) {
    return 'Start WebRTC Voice Call';
  }
  if (contactPhoneNumber.value) {
    return `Call ${contactPhoneNumber.value}`;
  }
  return t('CONVERSATION.HEADER.VOICE_CALL');
});

const startWhatsappCall = async () => {
  if (whatsappCallSession.isInitiating.value) return;
  try {
    const response = await whatsappCallSession.initiateOutboundCall({
      conversationId: props.chat.id,
    });

    if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
    if (!response?.id) {
      const status = response?.status;
      const messageKey =
        status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
          ? 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING'
          : 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED';
      useAlert(t(messageKey));
      return;
    }

    callsStore.addCall({
      callSid: response.call_id,
      callId: response.id,
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED'));
  }
};

const startTwilioCall = async () => {
  if (contactsUiFlags.value?.isInitiatingCall) return;
  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.chat?.meta?.sender?.id,
      inboxId: props.inbox?.id,
      conversationId: props.chat.id,
    });

    callsStore.addCall({
      callSid: response?.call_sid,
      conversationId: response?.conversation_id ?? props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.VOICE_CALL_FAILED'));
  }
};

const startWebRTCCall = () => {
  import('shared/helpers/mitt').then(({ emitter }) => {
    emitter.emit('webrtc:start-call', {
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
    });
  });
};

const startFreeswitchCall = async (targetLine = null) => {
  const phone = contactPhoneNumber.value;
  if (!phone) {
    useAlert(
      t('CONTACT_PANEL.PHONE_NOT_FOUND') ||
        'Contact phone number required to place call'
    );
    return;
  }

  // If this inbox is NOT a voice inbox and account has MULTIPLE voice lines, prompt user to select
  const isDirectVoiceInbox =
    voiceCallProvider.value === VOICE_CALL_PROVIDERS.FREESWITCH;
  if (!isDirectVoiceInbox && voiceInboxes.value.length > 1 && !targetLine) {
    showSimPickerDialog.value = true;
    return;
  }

  const line =
    targetLine ||
    (isDirectVoiceInbox
      ? {
          inboxId: props.inbox?.id,
          callerId: props.inbox?.telephony_number || props.inbox?.phone_number,
        }
      : (voiceInboxes.value.find(l => l.callerId) || voiceInboxes.value[0]));
  const inboxId = line?.inboxId || line?.id || props.inbox?.id;
  let callerId = line?.callerId || props.inbox?.telephony_number || props.inbox?.phone_number || '';
  if (!callerId && line?.displayNumber) {
    callerId = String(line.displayNumber).replace(/[^\d+]/g, '');
  }
  const contactName =
    props.chat?.meta?.sender?.name || props.chat?.sender?.name || phone;

  try {
    const callSid = await FreeswitchVoiceClient.makeCall(phone, {
      inboxId,
      callerId,
      contactId: props.chat?.meta?.sender?.id || props.chat?.sender?.id,
      conversationId: props.chat.id,
    });

    callsStore.addCall({
      callSid,
      callId: callSid,
      conversationId: props.chat.id,
      inboxId,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.FREESWITCH,
      caller: { name: contactName, phone },
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.VOICE_CALL_FAILED'));
  }
};

const selectLineAndCall = (inbox) => {
  showSimPickerDialog.value = false;
  startFreeswitchCall(inbox);
};

const startCall = () => {
  if (isWhatsappVoiceInbox.value) {
    return startWhatsappCall();
  }
  if (isWebWidgetWithCalling.value && !contactPhoneNumber.value) {
    return startWebRTCCall();
  }
  if (isFreeswitchCall.value) {
    return startFreeswitchCall();
  }
  if (voiceCallProvider.value === VOICE_CALL_PROVIDERS.TWILIO) {
    return startTwilioCall();
  }
  return startFreeswitchCall();
};
</script>

<template>
  <div v-if="isVoiceCallInbox" class="inline-flex items-center">
    <NextButton
      v-tooltip.bottom="callButtonTooltip"
      sm
      ghost
      slate
      icon="i-lucide-phone"
      :is-loading="isCallButtonLoading"
      :disabled="isCallButtonDisabled"
      @click="startCall"
    />

    <!-- Outbound Number Selection Dialog (Triggered if multiple account voice numbers exist) -->
    <Dialog
      v-if="showSimPickerDialog"
      title="Select Outbound Number"
      :description="`Choose which number you want to initiate the call to ${contactPhoneNumber} from:`"
      :show-confirm-button="false"
      cancel-button-label="Cancel"
      @cancel="showSimPickerDialog = false"
    >
      <div class="flex flex-col gap-2.5 py-2">
        <div
          v-for="line in voiceInboxes"
          :key="line.id"
          class="flex items-center justify-between p-3.5 rounded-2xl border bg-n-surface-1 border-n-weak hover:border-n-teal-9 hover:bg-n-teal-3/10 transition-all cursor-pointer group"
          @click="selectLineAndCall(line)"
        >
          <div class="flex items-center gap-3.5 min-w-0">
            <div
              class="flex items-center justify-center size-10 rounded-xl bg-n-teal-3 text-n-teal-11 font-bold text-xs shrink-0 group-hover:scale-105 transition-transform"
            >
              <Icon icon="i-lucide-phone-outgoing" class="size-5" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12 truncate">
                {{ line.displayNumber || line.name }}
              </p>
              <p class="text-xs text-n-slate-10 truncate">
                {{ line.name }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <Button
              teal
              size="sm"
              icon="i-lucide-phone"
              label="Call with this number"
              class="!rounded-xl"
              @click.stop="selectLineAndCall(line)"
            />
          </div>
        </div>
      </div>
    </Dialog>
  </div>
  <template v-else />
</template>
